import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "../../../types/state/AppState";
import { isRequesting } from "../../../utils";
import { SORT, SORTING_TYPE, constants, issuanceMonitorWidget } from "../../../constants";
import { Table } from "../../bidding/common/table";
import { topTrusteesColumns } from "./columns/TopTrusteesColumns";
import { Pagination } from "../tables/Pagination";
import {
    DashboardWidget,
    DashboardWidgetContent,
    DashboardWidgetHeader,
} from "../widget";
import { DashboardSkeleton } from "../DashboardSkeleton";
import {
    dashboardFetchTopTrustees,
    dashboardExportTopTrustees,
} from "../../../actions/dashboard.actions";
import { useDispatch } from "react-redux";
import { TopChartCompany } from "../../../types/dashboard/TopChartCompany";
import { PipelineWidgetColumn } from "../../../types/dashboard/PipelineWidgetColumn";
import { getAvailableYears, getSortingParamsForExport } from "./top-list.utils";
import { DropDownList, DropDownListItem } from "../../controls/DropDownList";
import { YearSelector } from "../../controls/YearSelector";
import { DashboardNoSelectedFilters } from "../DashboardNoSelectedFilters";
import { useSortedList } from "../../../effects";
import { ShowFor } from "../../access";
import ImExportButton from "../../amrPipeline/common/ImExportButton";
import { SubscriptionFeature } from '../../../types/billing/SubscriptionFeature';
import { hasWidgetsAccess } from '../../../utils/amr-pipeline.utils';
import { CollateralTypesList } from "../../../constants/dashboard/collateral-types";
import { amrChartUtils } from "../../../utils/amr-chart.utils";
import { ChartRequiredFeature } from "../../access/ChartRequiredFeature";
import { TopIssuersCurrencyTypes, TopIssuersTransactionTypes } from './constants';

const availableYears: number[] = getAvailableYears(2013);

const getFilterValue = (value: string) =>
    value === constants.allListItem ? undefined : value;

interface Props {
    onListItemClick?: () => void;
}

export function TopTrusteesWidget({ onListItemClick }: Props) {
    const [type, setType] = useState(constants.allListItem);
    const [currency, setCurrency] = useState(constants.allListItem);
    const [year, setYear] = useState<number | undefined>(availableYears[0]);
    const [page, setPage] = useState(1);
    const [collateralType, setCollateralType] = useState(CollateralTypesList[0].value);

    const [sortBy, setSortBy] = useState("");
    const [sortDirection, setSortDirection] = useState(SORT.DESC);

    const topTrustees = useSelector((s: AppState) => s.dashboard.topTrustees);
    const requestStateFetchTopTrustees = useSelector(
        (s: AppState) => s.dashboard.requestStateFetchTopTrustees
    );
    const requestStateExportTopTrustees = useSelector(
        (s: AppState) => s.dashboard.requestStateExportTopTrustees
    );

    const hasAccess = hasWidgetsAccess(SubscriptionFeature.Top10TrusteesTable);

    const dispatch = useDispatch();

    useEffect(() => {
        if (!hasAccess) {
            return;
        }

        dispatch(
            dashboardFetchTopTrustees(
                getFilterValue(type),
                getFilterValue(currency),
                year,
                getFilterValue(collateralType)
            )
        );
        setPage(1);
    }, [dispatch, hasAccess, type, currency, year, collateralType])

    const columnContext = () => ({
        onListItemClick,
        currency,
    });

    const isSortByCompany = sortBy === PipelineWidgetColumn.company;
    const sortingType = isSortByCompany ? SORTING_TYPE.company : '';
    const [sortedDataItems] = useSortedList(
        topTrustees,
        sortBy,
        sortDirection,
        sortingType
    );

    const withSizeColumn = type !== constants.allListItem && currency !== constants.allListItem;

    const handleTypeChange = ({ value }: DropDownListItem) => setType(value.toString());
    const handleCurrencyChange = ({ value }: DropDownListItem) => setCurrency(value.toString());
    const handleYearChange = (yearValue?: number) => setYear(yearValue);

    const handleSortChange = (field: string, direction: string) => {
        setSortBy(field);
        setSortDirection(direction);
    };

    const handleExportChart = () =>
        dispatch(
            dashboardExportTopTrustees(
                getFilterValue(type),
                getFilterValue(currency),
                year,
                getFilterValue(collateralType),
                getSortingParamsForExport(sortBy, sortDirection),
            )
        );
    const onPageChanged = (selectedPage: number) => setPage(selectedPage);

    const tableColumns = withSizeColumn
        ? topTrusteesColumns.filter(c => c.column.columnKey !== PipelineWidgetColumn.emptySize)
        : topTrusteesColumns.filter(c => c.column.columnKey !== PipelineWidgetColumn.dealBalanceTotal);

    const renderTable = () => {
        const getTableWithData = (items: TopChartCompany[]) => (
            <Table
                onSort={handleSortChange}
                defaultSortBy={sortBy}
                defaultSortByDirection={sortDirection}
                dataItems={items}
                columns={tableColumns}
                createSecurityCustomArgs={columnContext}
                className="data-list-striped"
            />
        );

        return sortedDataItems.length > issuanceMonitorWidget.itemsOnPage ? (
            <Pagination
                list={sortedDataItems}
                itemsOnPage={issuanceMonitorWidget.itemsOnPage}
                currentPage={page}
                onPageChanged={onPageChanged}
            >
                {(items: TopChartCompany[]) =>  getTableWithData(items)}
            </Pagination>
        ) : getTableWithData(sortedDataItems);
    }

    const chartName = "Top-10 Trustees";

    return (
        <DashboardWidget title={chartName} className="top-issuers">
            <DashboardSkeleton inProgress={isRequesting(requestStateFetchTopTrustees)}>
                <ChartRequiredFeature
                    feature={SubscriptionFeature.Top10TrusteesTable}
                    chartName={chartName}
                    blockedClassName="restricted-placeholder-top-trustees"
                >
                    <DashboardWidgetHeader>
                        <h3>{chartName}</h3>
                        <ShowFor feature={SubscriptionFeature.Top10TrusteesTable}>
                            <div className="flex-item-right">
                                <ImExportButton
                                    isExportLoad={isRequesting(requestStateExportTopTrustees)}
                                    onExport={handleExportChart}
                                    disabled={!topTrustees.length}
                                />
                            </div>
                            <div className="flex-item-right control-box">
                                <div className="control-item">
                                    <DropDownList
                                        items={TopIssuersTransactionTypes.map(
                                            ({ value, text }) => ({
                                                value,
                                                text,
                                                selected: value === type,
                                            })
                                        )}
                                        onChange={handleTypeChange}
                                        className="custom-drop-down-ghost"
                                        prefix="Type: "
                                    />
                                </div>
                                <div className="control-item ccy-box">
                                    <DropDownList
                                        items={TopIssuersCurrencyTypes.map(({ value, text }) => ({
                                            value,
                                            text,
                                            selected: value === currency,
                                        }))}
                                        onChange={handleCurrencyChange}
                                        className="custom-drop-down-ghost"
                                        defaultSortingEnabled={false}
                                        prefix="Ccy: "
                                    />
                                </div>
                                <div className="control-item">
                                    <DropDownList
                                        items={CollateralTypesList.map(x => ({
                                            ...x,
                                            selected: collateralType === x.value,
                                        }))}
                                        defaultSortingEnabled={false}
                                        onChange={({ value }) => setCollateralType(value as string)}
                                        renderSelectedItemCallback={amrChartUtils.renderSelectedFilterOptionWithPrefix('Collat.')}
                                        className="custom-drop-down-ghost"
                                    />
                                </div>
                                <div className="control-item">
                                    <YearSelector
                                        years={availableYears}
                                        selectedYear={year}
                                        currentYearPrefix="YTD"
                                        onChange={handleYearChange}
                                        prefix="Year: "
                                        showAllOption
                                    />
                                </div>
                            </div>
                        </ShowFor>
                    </DashboardWidgetHeader>
                    <DashboardWidgetContent
                        requestState={requestStateFetchTopTrustees}
                        description={
                            sortedDataItems.length
                                ? "All statistics are calculated based on the transaction pricing date."
                                : ""
                        }
                    >
                        {sortedDataItems.length ? renderTable() : <DashboardNoSelectedFilters />}
                    </DashboardWidgetContent>
                </ChartRequiredFeature>
            </DashboardSkeleton>
        </DashboardWidget>
    );
}
